import { useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  FormTextInput,
  FormSelectInput,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { emailRegex, enumMapper } from '@wajeez-web/core/utils';
import {
  SalesActions,
  useAccountDispatch,
  useAccountSelector,
} from '@wajeez-web/stores/account';
import { BiLoaderAlt } from 'react-icons/bi';

type Input = {
  email: string;
  password: string;
  employeeName: string;
  role: Server.UtilityUserRole;
};

interface SaleUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void | Promise<void>;
}

export const SaleUserModal = ({
  isOpen,
  onClose,
  onSubmit,
}: SaleUserModalProps) => {
  const accountDispatch = useAccountDispatch();

  const createSalesUserStatus = useAccountSelector(
    ({ sales }) => sales.createUtilityUser.status
  );

  const { handleSubmit, control } = useForm<Input>();

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    const res = await accountDispatch(
      SalesActions.createUtilityUserAsync({ ...data, role: Number(data.role) })
    );

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit && (await onSubmit());
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Sale User"
      alertTarget="modal"
    >
      <div className="w-full">
        <form
          onSubmit={handleSubmit(onFormSubmit)}
          noValidate
          className="w-full flex flex-col justify-start items-start"
        >
          <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
            <FormTextInput
              name="email"
              control={control}
              fieldType={{
                fieldType: Server.FieldType.Text,
                fieldOptions: { pattern: emailRegex() },
                required: true,
                label: 'Email',
              }}
              errorMessages={{
                required: 'This field is required',
                pattern: 'This email is invalid',
              }}
              readOnly
              heightClassName="h-[34px]"
              widthClassName="w-full"
            />
            <FormTextInput
              name="password"
              control={control}
              fieldType={{
                fieldType: Server.FieldType.Text,
                fieldOptions: {},
                required: true,
                label: 'Password',
              }}
              errorMessages={{ required: 'This field is required' }}
              readOnly
              heightClassName="h-[34px]"
              widthClassName="w-full"
              inputtype="password"
            />
            <FormTextInput
              name="employeeName"
              control={control}
              fieldType={{
                fieldType: Server.FieldType.Text,
                fieldOptions: {},
                required: true,
                label: 'Employee Name',
              }}
              errorMessages={{ required: 'This field is required' }}
              heightClassName="h-[34px]"
              widthClassName="w-full"
            />
            <FormSelectInput
              name="role"
              control={control}
              fieldType={{
                fieldType: Server.FieldType.Select,
                fieldOptions: {
                  selectOptions: enumMapper(Server.UtilityUserRole).map((b) => {
                    return {
                      value: b.value,
                      label: b.label,
                    };
                  }),
                },
                required: true,
                label: 'Branch',
              }}
              errorMessages={{ required: 'This field is required' }}
              widthClassName="w-full"
              showErrors
            />
          </div>
          <div className="mt-6 w-full flex justify-end items-center space-s-2">
            <div className="w-[70px]">
              <ButtonHeadless
                bgStyle="bg-warmGray-300"
                borderStyle="border-none"
                paddingYStyle="py-1"
                action={onClose}
              >
                <div className="text-warmGray-600 text-[16px] font-medium">
                  <ReturnTranslatedText
                    text="buttons.Cancel"
                    origintext="Cancel"
                  />
                </div>
              </ButtonHeadless>
            </div>
            <div className="w-[70px]">
              <ButtonHeadless
                type="submit"
                borderStyle="border-none"
                paddingYStyle="py-1"
              >
                <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                  <ReturnTranslatedText text="buttons.Save" origintext="Save" />
                  {createSalesUserStatus === 'loading' && (
                    <BiLoaderAlt className="animate-spin w-5" />
                  )}
                </div>
              </ButtonHeadless>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};
